import { configureStore, combineReducers, AnyAction } from '@reduxjs/toolkit';
import { LibraryReduxSlices } from './types';
import { shareSlice } from '../components/share-modal/redux/reducer';
import { headerSlice } from '../components/header/redux/reducer';
import { reportsTitleBarSlice } from '../components/reports-title-bar/redux/reducer';
import { inventoryReportsTitleBarSlice } from '../components/inventory-reports-title-bar/redux/reducer';
import { dashboardSlice } from '../views/afterAuth/dashboard/redux/reducer';
import { formBuilderListSlice } from '../views/form-builder/redux/reducer';

// Default library reducers
export const libraryReducers = {
  share: shareSlice.reducer,
  header: headerSlice.reducer,
  reportsTitleBar: reportsTitleBarSlice.reducer,
  inventoryReportsTitleBar: inventoryReportsTitleBarSlice.reducer,
  dashboard: dashboardSlice.reducer,
  formBuilder: formBuilderListSlice.reducer,
};

export type ReducerFn<State = Record<string, unknown>> = (
  state: State | undefined,
  action: AnyAction
) => State;
type ReducerMap = Record<string, ReducerFn>;

export type ReducerManager = {
  getReducers: () => ReducerMap;
  reduce: ReducerFn;
  add: (key: string, reducer: ReducerFn) => void;
  remove: (key: string) => void;
};

const createReducerManager = (initialReducers: ReducerMap): ReducerManager => {
  let reducers = { ...initialReducers };
  let combinedReducer = combineReducers(reducers);
  let keysToRemove: string[] = [];

  return {
    getReducers: () => ({ ...reducers }),
    reduce: (state: any, action: AnyAction) => {
      if (keysToRemove.length) {
        state = { ...state };
        keysToRemove.forEach((key) => delete state[key]);
        keysToRemove = [];
      }
      return combinedReducer(state, action);
    },
    add: (key: string, reducer: ReducerFn) => {
      if (!key || reducers[key]) return;
      reducers = { ...reducers, [key]: reducer };
      combinedReducer = combineReducers(reducers);
    },
    remove: (key: string) => {
      if (!key || !reducers[key]) return;
      const { [key]: _, ...rest } = reducers;
      reducers = rest;
      keysToRemove.push(key);
      combinedReducer = combineReducers(reducers);
    },
  };
};

type ReducerInjectorStore = ReturnType<typeof configureStore> & {
  reducerManager: ReducerManager;
  injectReducer: (key: string, reducer: ReducerFn) => void;
  removeReducer: (key: string) => void;
};

// Helper function to create a store with library slices
export const createLibraryStore = (
  additionalReducers: Record<string, any> = {},
  apiClient?: any,
  middleware: any[] = []
) => {
  const reducerManager = createReducerManager({
    ...libraryReducers,
    ...additionalReducers,
  });

  const store = configureStore({
    reducer: reducerManager.reduce,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: { apiClient },
        },
      }).concat(middleware),
  });

  const reducerEnabledStore: ReducerInjectorStore = {
    ...store,
    reducerManager,
    injectReducer: (key: string, reducer: ReducerFn) => {
      reducerManager.add(key, reducer);
      store.replaceReducer(reducerManager.reduce);
    },
    removeReducer: (key: string) => {
      reducerManager.remove(key);
      store.replaceReducer(reducerManager.reduce);
    },
  };

  return reducerEnabledStore;
};

// Helper function to get just the library slices for integration with existing stores
export const getLibrarySlices = (): LibraryReduxSlices => {
  return {
    share: shareSlice.reducer,
    header: headerSlice.reducer,
    reportsTitleBar: reportsTitleBarSlice.reducer,
    inventoryReportsTitleBar: inventoryReportsTitleBarSlice.reducer,
    dashboard: dashboardSlice.reducer,
    formBuilder: formBuilderListSlice.reducer,
  };
};

// Type for the library store
export type LibraryStore = ReturnType<typeof createLibraryStore>;
export type LibraryRootState = ReturnType<LibraryStore['getState']>;
export type LibraryDispatch = LibraryStore['dispatch'];

// Default store instance for consumers that want a ready-to-use store
export const store: LibraryStore = createLibraryStore();

// Helpers for consuming apps to inject or remove reducers at runtime
export const injectLibraryReducer = (
  store: LibraryStore,
  key: string,
  reducer: ReducerFn
) => {
  if (typeof store.injectReducer === 'function') {
    store.injectReducer(key, reducer);
  }
};

export const removeLibraryReducer = (store: LibraryStore, key: string) => {
  if (typeof store.removeReducer === 'function') {
    store.removeReducer(key);
  }
};